import './style.css';
import { computeQimen, type QimenResult } from './engine/qimen';
import stemCombos from './data/stem_combos.json';
import {
  emptyCustomer,
  loadRecords,
  saveRecord,
  deleteRecord,
  type CustomerInfo,
  type SavedRecord,
} from './lib/records';
import {
  searchCuiyun,
  formatBoardTime,
  summarizeHits,
  normalizeCrossLayer,
  type CuiyunBoard,
  type CuiyunSearchResult,
  type RelationFilter,
  type LayerItemFilters,
  type CrossLayerRel,
  type CrossLayerSide,
  type CrossLayerFilter,
} from './lib/cuiyun';
import {
  type CuiyunLayer,
  LAYER_LABEL,
  LAYER_ITEMS,
  layerItemLabel,
  normalizeLayerItem,
} from './lib/wuxing';

const app = document.querySelector<HTMLDivElement>('#app')!;

const now = new Date();
const _qs = new URLSearchParams(location.search);
const _num = (k: string, fallback: number) => {
  const v = _qs.get(k);
  if (v == null || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
let state = {
  year: _num('y', now.getFullYear()),
  month: _num('m', now.getMonth() + 1),
  day: _num('d', now.getDate()),
  hour: _num('h', now.getHours()),
  minute: _num('mi', now.getMinutes()),
  clockwise: 0,
  antiClockwise: 0,
};

let customer: CustomerInfo = emptyCustomer();
/** When set, next「儲存」updates this id instead of creating a new record. */
let editingId: string | null = null;
let statusMsg = '';
let exportBusy = false;

/** 可催運盤 quick search */
const nowCui = new Date();
function defaultCuiRange() {
  const from = {
    year: nowCui.getFullYear(),
    month: nowCui.getMonth() + 1,
    day: nowCui.getDate(),
    hour: 0,
    minute: 0,
  };
  const end = new Date(nowCui.getFullYear(), nowCui.getMonth(), nowCui.getDate() + 7, 23, 59);
  const to = {
    year: end.getFullYear(),
    month: end.getMonth() + 1,
    day: end.getDate(),
    hour: 23,
    minute: 59,
  };
  return { from, to };
}
const _cuiInit = defaultCuiRange();
let cuiFrom = { ..._cuiInit.from };
let cuiTo = { ..._cuiInit.to };
let cuiLayers: Set<CuiyunLayer> = new Set(['gan', 'men', 'xing', 'shen']);
/** 各層細項選集（空＝該層不限細項） */
let cuiLayerItems: Record<CuiyunLayer, Set<string>> = {
  gan: new Set(),
  men: new Set(),
  xing: new Set(),
  shen: new Set(),
};
let cuiRelation: RelationFilter = null;
let cuiResult: CuiyunSearchResult | null = null;
let cuiBusy = false;
/** Entire 可催運盤 section collapsed by default */
let cuiyunSectionExpanded = false;
/** Filter selection block (層／生剋／細項) collapsed by default; nests inside section */
let cuiFiltersExpanded = false;
/**
 * 跨層生剋（洛書宮位五行）：A + 生|剋 + B；三者齊備才過濾。
 * 比對 A／B 所在宮洛書五行（非符號固有五行）。samePalace 預設 false。
 */
let cuiCrossA: CrossLayerSide | null = null;
let cuiCrossRel: CrossLayerRel | null = null;
let cuiCrossB: CrossLayerSide | null = null;
let cuiCrossSamePalace = false;
/** 跨層選端時目前編輯的層（A／B 各自） */
let cuiCrossALayer: CuiyunLayer = 'gan';
let cuiCrossBLayer: CuiyunLayer = 'xing';
/** Highlight mode on current chart */
let cuiHighlight: RelationFilter = null;
let cuiHighlightPalaces: Set<number> = new Set();
/** 跨層高亮（載入時） */
let cuiCrossHighlight = false;

/** Overlay labels: exactly 8 groups */
const OVERLAY_KINDS = [
  '日干',
  '時干',
  '年干',
  '月干',
  '日根',
  '時根',
  '年根',
  '月根',
] as const;
type OverlayKind = (typeof OVERLAY_KINDS)[number];

/** Per-palace overlay labels (max 6 each). Key = luoshu 1–9. */
const overlays: Record<number, OverlayKind[]> = {
  1: [],
  2: [],
  3: [],
  4: [],
  5: [],
  6: [],
  7: [],
  8: [],
  9: [],
};

let overlayPalace = 1;
let overlayKind: OverlayKind = '日干';

/** Grid-position colors (Excel Worksheet fills by NW…SE slot, not by 洛書 identity). */
const SLOT_TONE = ['blue', 'yellow', 'blue', 'yellow', 'yellow', 'yellow', 'blue', 'yellow', 'blue'] as const;

const COMBO_MAP = stemCombos as Record<string, string>;

function numInput(id: string, label: string, value: number, min: number, max: number): string {
  return `<label>${label}<input id="${id}" type="number" min="${min}" max="${max}" value="${value}" /></label>`;
}

function textInput(
  id: string,
  label: string,
  value: string,
  opts?: { placeholder?: string; wide?: boolean },
): string {
  const ph = opts?.placeholder ? ` placeholder="${esc(opts.placeholder)}"` : '';
  const cls = opts?.wide ? ' class="input-wide"' : '';
  return `<label class="field-text">${label}<input id="${id}" type="text"${cls} value="${esc(value)}"${ph} autocomplete="off" /></label>`;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Excel never shows 禽; display 芮 (matches engine starLabel / AC18). */
function starLabel(s: string): string {
  return s === '禽' ? '芮' : s;
}

function lookupCombo(tian: string, di: string): string | null {
  if (!tian || !di) return null;
  const key = tian + di;
  const text = COMBO_MAP[key];
  return text && !key.startsWith('_') ? text : null;
}

/** Collect 天干組合 that appear on the plate (天盤干／寄顯 vs 地盤干／寄顯). */
function plateCombos(result: QimenResult): { palace: number; name: string; pair: string; text: string }[] {
  const out: { palace: number; name: string; pair: string; text: string }[] = [];
  const seen = new Set<string>();
  for (const p of result.palaces) {
    if (p.luoshu === 5) continue;
    const tianStems = [p.tianGan, p.jiXianTian].filter(Boolean);
    const diStems = [p.diGan, p.jiXianDi].filter(Boolean);
    for (const t of tianStems) {
      for (const d of diStems) {
        const text = lookupCombo(t, d);
        if (!text) continue;
        const pair = `${t}+${d}`;
        const id = `${p.luoshu}:${pair}`;
        if (seen.has(id)) continue;
        seen.add(id);
        out.push({ palace: p.luoshu, name: p.name, pair, text });
      }
    }
  }
  return out;
}

function clearOverlays(): void {
  for (let i = 1; i <= 9; i++) overlays[i] = [];
}

function cloneOverlays(): Record<number, OverlayKind[]> {
  const out: Record<number, OverlayKind[]> = {};
  for (let i = 1; i <= 9; i++) out[i] = [...(overlays[i] ?? [])];
  return out;
}

function applyOverlays(src: Record<number, string[]> | undefined): void {
  clearOverlays();
  if (!src) return;
  for (let i = 1; i <= 9; i++) {
    const list = src[i] ?? [];
    overlays[i] = list.filter((k): k is OverlayKind =>
      (OVERLAY_KINDS as readonly string[]).includes(k),
    );
  }
}

function formatSavedAt(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return iso;
  }
}

function readCustomerFromDom(): void {
  const g = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
  customer = {
    name: g('cust-name').trim(),
    phone: g('cust-phone').trim(),
    gender: g('cust-gender').trim(),
    purpose: g('cust-purpose').trim(),
    note: g('cust-note').trim(),
  };
}

function renderCenterPalace(result: QimenResult, luoshu: number, slotIndex: number): string {
  const p = result.palaces.find((x) => x.luoshu === luoshu)!;
  const tone = SLOT_TONE[slotIndex] ?? 'yellow';
  const juShort = `${result.dun === '阳遁' ? '阳' : '阴'}${result.juLabel}`;
  const xunLine = `${result.xunshou}${result.xunshouStem}旬`;
  const hourLine = `${result.pillars.hour}時`;
  const ov = overlays[luoshu] ?? [];
  const ovHtml = ov.map((k) => `<span class="ov-tag">${esc(k)}</span>`).join('');

  return `<div class="palace center tone-${tone}" data-luoshu="${luoshu}">
    <div class="p-ov" title="標註（最多6）">${ovHtml}</div>
    <div class="c-meta">
      <div class="c-ju">${esc(juShort)}</div>
      <div class="c-xun">${esc(xunLine)}</div>
      <div class="c-hour">${esc(hourLine)}</div>
    </div>
    <div class="c-zf">
      <span class="c-lbl">值符</span><span class="c-val">${esc(starLabel(result.zhifu.star))}</span>
      <span class="c-lbl">值使</span><span class="c-val">${esc(result.zhishi.door)}</span>
    </div>
    <div class="c-stems" title="中宮干星">
      <span class="di-gan">${esc(p.diGan || '·')}</span>
      <span class="tian-xing">${esc(starLabel(p.tianPanXing || p.star || '') || '·')}</span>
      <span class="tian-gan">${esc(p.tianGan || '·')}</span>
    </div>
  </div>`;
}

/**
 * Palace cell layout (refs Fig1–3 + user rules):
 *   upper-right = 地盤星 + 洛書號
 *   center-top cluster = 神（天／地八神）
 *   dead-center = 天盤星
 *   right = 天盤天干（兩干則橫排）；其下 = 地盤天干
 *   mid-bottom = 門
 *   left-mid = 引干（綠；兩干則直排）
 *   bottom = 馬迫墓刑空（紅）
 *   overlays reserved at top (max 6)
 */
function renderPalaceCell(result: QimenResult, luoshu: number, slotIndex: number): string {
  if (luoshu === 5) return renderCenterPalace(result, luoshu, slotIndex);

  const p = result.palaces.find((x) => x.luoshu === luoshu)!;
  const isZhifu = result.zhifu.palace === luoshu;
  const isZhishi = result.zhishi.palace === luoshu;
  const tone = SLOT_TONE[slotIndex] ?? 'blue';
  // Avoid duplicating 八神「符」: only badge 使; 值符 palace gets class hook instead
  const marks = isZhishi ? '使' : '';
  const tianXing = starLabel(p.tianPanXing || p.star || '');
  const diXing = starLabel(p.diPanXing || '');

  // 神：天盤八神 + 地盤八神（宮頂／視覺中上）
  const shenParts = [p.shen, p.diShen].filter(Boolean);
  const shenHtml = shenParts.length
    ? shenParts.map((s) => `<span class="shen">${esc(s)}</span>`).join('')
    : '<span class="shen muted">·</span>';

  // 天盤天干：本干 + 寄顯 → 橫排
  const tianGans = [p.tianGan, p.jiXianTian].filter(Boolean);
  const tianGanHtml = tianGans.length
    ? tianGans.map((g) => `<span class="tian-gan">${esc(g)}</span>`).join('')
    : '<span class="tian-gan">·</span>';

  // 地盤天干：本干 + 寄顯（寄顯較小）
  const diGanHtml = p.diGan
    ? `<span class="di-gan">${esc(p.diGan)}</span>${
        p.jiXianDi ? `<span class="jx" title="寄顯（地）">${esc(p.jiXianDi)}</span>` : ''
      }`
    : '<span class="di-gan">·</span>';

  // 引干：綠；兩干直排。第二干对拍 Program A11：
  //   AR17=是且引干=AR坤 → AR中；否则非AR17且引干=坤地盤 → 中地盤（寄坤）；AR17时寄坤抑制。
  const yinKun = result.palaces.find((x) => x.luoshu === 2)?.yinGan ?? '';
  const yinZhong = result.palaces.find((x) => x.luoshu === 5)?.yinGan ?? '';
  const kunDi = result.palaces.find((x) => x.luoshu === 2)?.diGan ?? '';
  const zhongDi = result.palaces.find((x) => x.luoshu === 5)?.diGan ?? '';
  let yinExtra = '';
  if (p.yinGan && result.xunshouInZhong && yinKun && p.yinGan === yinKun) {
    yinExtra = yinZhong;
  } else if (p.yinGan && !result.xunshouInZhong && kunDi && p.yinGan === kunDi) {
    yinExtra = zhongDi;
  }
  const yinList = [p.yinGan, yinExtra].filter(Boolean);
  const yinHtml = yinList.length
    ? yinList.map((g) => `<span class="yin-gan">${esc(g)}</span>`).join('')
    : '';

  // 底標：馬迫墓刑空 — 一律紅色
  const tags = [
    p.maXing ? '<span class="mk ma">馬</span>' : '',
    p.po ? '<span class="mk po">迫</span>' : '',
    p.mu ? '<span class="mk mu">墓</span>' : '',
    p.xing ? '<span class="mk xing">刑</span>' : '',
    p.kongWang ? '<span class="mk kong">空</span>' : '',
  ]
    .filter(Boolean)
    .join('');

  const ov = overlays[luoshu] ?? [];
  const ovHtml = ov.map((k) => `<span class="ov-tag">${esc(k)}</span>`).join('');

  const hitSheng = cuiHighlight === '生我' && cuiHighlightPalaces.has(luoshu);
  const hitKe = cuiHighlight === '我剋' && cuiHighlightPalaces.has(luoshu);
  const hitBen = cuiHighlight === '本宮' && cuiHighlightPalaces.has(luoshu);
  const hitCross = cuiCrossHighlight && cuiHighlightPalaces.has(luoshu);
  const hitCls = hitCross
    ? ' hit-cross'
    : hitBen
      ? ' hit-ben'
      : hitSheng
        ? ' hit-sheng'
        : hitKe
          ? ' hit-ke'
          : '';

  return `<div class="palace tone-${tone}${isZhifu ? ' is-zhifu' : ''}${isZhishi ? ' is-zhishi' : ''}${hitCls}" data-luoshu="${luoshu}">
<div class="p-ov" title="標註（最多6）">${ovHtml}</div>
    <div class="p-corner">
      ${marks ? `<span class="p-mark">${esc(marks)}</span>` : ''}
      <span class="di-xing" title="地盤星">${esc(diXing || '')}</span>
      <span class="p-luo">${p.luoshu}</span>
    </div>
    <div class="p-shen" title="神">${shenHtml}</div>
    <div class="p-body">
      <div class="p-yin" title="引干">${yinHtml}</div>
      <div class="p-core">
        <span class="tian-xing" title="天盤星">${esc(tianXing || '·')}</span>
        <span class="door" title="門">${esc(p.door || '·')}</span>
      </div>
      <div class="p-stems">
        <div class="tian-gan-row" title="天盤天干">${tianGanHtml}</div>
        <div class="di-gan-row" title="地盤天干">${diGanHtml}</div>
      </div>
    </div>
    <div class="p-tags">${tags || '<span class="mk muted"> </span>'}</div>
    <div class="p-name">${esc(p.name)}</div>
  </div>`;
}

function readCuiFromDom(): void {
  const n = (id: string, fallback: number) => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el) return fallback;
    const v = Number(el.value);
    return Number.isFinite(v) ? v : fallback;
  };
  cuiFrom = {
    year: n('cui-from-year', cuiFrom.year),
    month: n('cui-from-month', cuiFrom.month),
    day: n('cui-from-day', cuiFrom.day),
    hour: n('cui-from-hour', cuiFrom.hour),
    minute: n('cui-from-minute', cuiFrom.minute),
  };
  cuiTo = {
    year: n('cui-to-year', cuiTo.year),
    month: n('cui-to-month', cuiTo.month),
    day: n('cui-to-day', cuiTo.day),
    hour: n('cui-to-hour', cuiTo.hour),
    minute: n('cui-to-minute', cuiTo.minute),
  };
}


function currentCrossLayerFilter(): CrossLayerFilter | null {
  if (!cuiCrossA || !cuiCrossB || !cuiCrossRel) return null;
  return normalizeCrossLayer({
    a: cuiCrossA,
    relation: cuiCrossRel,
    b: cuiCrossB,
    samePalace: cuiCrossSamePalace,
  });
}

function crossLayerHint(): string {
  const f = currentCrossLayerFilter();
  if (!f) {
    if (cuiCrossRel || cuiCrossA || cuiCrossB) {
      return '跨層生剋未齊（須選 A、生/剋、B）';
    }
    return '跨層生剋關';
  }
  const aLab = layerItemLabel(f.a.layer, f.a.id);
  const bLab = layerItemLabel(f.b.layer, f.b.id);
  return `${aLab}宮洛書${f.relation}${bLab}宮洛書${f.samePalace ? ' · 須同宮' : ' · 異宮可'}`;
}

function runCuiyunSearch(): void {
  readCuiFromDom();
  cuiBusy = true;
  statusMsg = '正在搜尋可催運盤…';
  // sync paint then search (may be heavy)
  render();
  try {
    const layerItems: LayerItemFilters = {};
    for (const layer of ['gan', 'men', 'xing', 'shen'] as CuiyunLayer[]) {
      if (cuiLayerItems[layer].size) {
        layerItems[layer] = [...cuiLayerItems[layer]];
      }
    }
    cuiResult = searchCuiyun({
      from: { ...cuiFrom },
      to: { ...cuiTo },
      layers: [...cuiLayers],
      layerItems,
      relation: cuiRelation,
      crossLayer: currentCrossLayerFilter(),
      maxSlots: 450,
    });
    const n = cuiResult.boards.length;
    statusMsg = `可催運盤：找到 ${n} 盤（掃描 ${cuiResult.scanned} 時辰；排除伏／反吟 ${cuiResult.excludedFuyin}、本宮凶 ${cuiResult.excludedDayPalace}、生我宮凶 ${cuiResult.excludedShengWoBad}、庚丙 ${cuiResult.excludedGengBing}${cuiResult.truncated ? '；已截斷' : ''}）`;
  } catch (err) {
    console.error(err);
    cuiResult = null;
    statusMsg = `搜尋失敗：${err instanceof Error ? err.message : String(err)}`;
  } finally {
    cuiBusy = false;
    render();
  }
}

function loadCuiyunBoard(board: CuiyunBoard): void {
  state = {
    ...state,
    year: board.year,
    month: board.month,
    day: board.day,
    hour: board.hour,
    minute: board.minute,
  };
  // highlight according to current relation；若有跨層命中則優先／並用跨層宮
  cuiCrossHighlight = false;
  if (board.crossLayerHit && currentCrossLayerFilter()) {
    cuiCrossHighlight = true;
    cuiHighlight = null;
    cuiHighlightPalaces = new Set(board.crossLayerHit.highlightPalaces);
  } else if (cuiRelation === '本宮') {
    cuiHighlight = '本宮';
    cuiHighlightPalaces = new Set(
      board.dayStemPalace != null ? [board.dayStemPalace] : [],
    );
  } else if (cuiRelation === '我剋') {
    cuiHighlight = '我剋';
    cuiHighlightPalaces = new Set(board.woKe.map((h) => h.luoshu));
  } else if (cuiRelation === '生我') {
    cuiHighlight = '生我';
    cuiHighlightPalaces = new Set(board.shengWo.map((h) => h.luoshu));
  } else {
    // 不限：預設高亮生我宮
    cuiHighlight = '生我';
    cuiHighlightPalaces = new Set(board.shengWo.map((h) => h.luoshu));
  }
  const hlLabel = cuiCrossHighlight
    ? `跨層${board.crossLayerHit?.label ?? ''}`
    : cuiRelation === '本宮'
      ? '本宮'
      : cuiRelation === '我剋'
        ? '我剋'
        : cuiRelation === '生我'
          ? '生我'
          : '生我';
  const crossNote = board.crossLayerHit
    ? `；跨層${board.crossLayerHit.label} @ ${board.crossLayerHit.highlightPalaces.join(',')}`
    : '';
  statusMsg = `已載入可催運盤 ${formatBoardTime(board)}；日干${board.dayStem}（${board.dayWx}）落${board.dayStemPalaceName}${board.dayStemPalace ?? ''}（宮${board.meWx}）；高亮「${hlLabel}」宮${crossNote}`;
  render();
}


function renderCrossLayerFilter(): string {
  const layerTabs = (side: 'a' | 'b', cur: CuiyunLayer) =>
    (['gan', 'men', 'xing', 'shen'] as CuiyunLayer[])
      .map((l) => {
        const on = cur === l;
        return `<button type="button" data-act="cui-cross-layer" data-side="${side}" data-layer="${l}" class="cui-item ${on ? 'active' : ''}">${LAYER_LABEL[l]}</button>`;
      })
      .join('');

  const itemChips = (side: 'a' | 'b', layer: CuiyunLayer, selected: CrossLayerSide | null) =>
    LAYER_ITEMS[layer]
      .map((o) => {
        const on = selected?.layer === layer && selected.id === o.id;
        return `<button type="button" data-act="cui-cross-item" data-side="${side}" data-layer="${layer}" data-item="${o.id}" class="cui-item ${on ? 'active' : ''}">${esc(o.label)}</button>`;
      })
      .join('');

  const relBtn = (rel: CrossLayerRel | null, label: string) => {
    const on = cuiCrossRel === rel;
    return `<button type="button" data-act="cui-cross-rel" data-rel="${rel ?? ''}" class="${on ? 'active' : ''}">${label}</button>`;
  };

  const aSel = cuiCrossA ? layerItemLabel(cuiCrossA.layer, cuiCrossA.id) : '—';
  const bSel = cuiCrossB ? layerItemLabel(cuiCrossB.layer, cuiCrossB.id) : '—';
  const relSel = cuiCrossRel ?? '關';

  return `<div class="cui-cross-block">
    <div class="cui-cross-title">跨層生剋 <span class="notes">（洛書宮位五行：A所在宮 ${esc(relSel)} B所在宮；非固有五行）</span></div>
    <div class="cui-cross-side">
      <span class="cui-item-label">A</span>
      ${layerTabs('a', cuiCrossALayer)}
      <button type="button" data-act="cui-cross-clear" data-side="a" class="cui-item cui-item-any ${!cuiCrossA ? 'active' : ''}">關</button>
      ${itemChips('a', cuiCrossALayer, cuiCrossA)}
    </div>
    <div class="btn-row cui-cross-rel">
      <span class="notes">關係</span>
      ${relBtn('生', '生')}
      ${relBtn('剋', '剋')}
      ${relBtn(null, '關')}
      <label class="cui-same-palace"><input type="checkbox" data-act="cui-cross-same" ${cuiCrossSamePalace ? 'checked' : ''}/> 須同宮</label>
    </div>
    <div class="cui-cross-side">
      <span class="cui-item-label">B</span>
      ${layerTabs('b', cuiCrossBLayer)}
      <button type="button" data-act="cui-cross-clear" data-side="b" class="cui-item cui-item-any ${!cuiCrossB ? 'active' : ''}">關</button>
      ${itemChips('b', cuiCrossBLayer, cuiCrossB)}
    </div>
    <p class="notes cui-cross-help">選定例：「乙剋芮」＝天干乙＋剋＋星芮 → 乙落宮洛書剋飛布芮落宮洛書；「合生乙」＝神合＋生＋天干乙。中宮排除。目前：${esc(aSel)} ${esc(relSel)} ${esc(bSel)}${cuiCrossSamePalace ? '（須同宮）' : ''}。</p>
  </div>`;
}

function renderCuiyunPanel(): string {
  const layerBtn = (id: CuiyunLayer, label: string) => {
    const on = cuiLayers.has(id);
    return `<button type="button" data-act="cui-layer" data-layer="${id}" class="${on ? 'active' : ''}">${label}</button>`;
  };
  const relBtn = (rel: RelationFilter, label: string) => {
    const on = cuiRelation === rel;
    return `<button type="button" data-act="cui-rel" data-rel="${rel ?? ''}" class="${on ? 'active' : ''}">${label}</button>`;
  };

  const itemPickerRow = (layer: CuiyunLayer) => {
    if (!cuiLayers.has(layer)) return '';
    const opts = LAYER_ITEMS[layer];
    const selected = cuiLayerItems[layer];
    const chips = opts
      .map((o) => {
        const on = selected.has(o.id);
        return `<button type="button" data-act="cui-item" data-layer="${layer}" data-item="${o.id}" class="cui-item ${on ? 'active' : ''}">${esc(o.label)}</button>`;
      })
      .join('');
    const anyOn = selected.size === 0;
    return `<div class="cui-item-row">
      <span class="cui-item-label">${LAYER_LABEL[layer]}</span>
      <button type="button" data-act="cui-item-any" data-layer="${layer}" class="cui-item cui-item-any ${anyOn ? 'active' : ''}">不限</button>
      ${chips}
    </div>`;
  };

  let listHtml = '';
  if (cuiResult) {
    if (!cuiResult.boards.length) {
      listHtml = `<p class="notes">此區間無通過篩選之盤。可放寬細項／「生我／我剋／本宮」或檢查日時範圍。</p>`;
    } else {
      const rows = cuiResult.boards
        .map((b, idx) => {
          const sw = summarizeHits(b.shengWo);
          const wk = summarizeHits(b.woKe);
          const bg = b.benGongHit ? summarizeHits([b.benGongHit]) : '—';
          const bgLine =
            cuiRelation === '本宮' || b.benGongHit
              ? `<div class="cui-sub notes"><span class="cui-tag ben">本宮</span> ${esc(bg)}</div>`
              : '';
          const crossLine = b.crossLayerHit
            ? `<div class="cui-sub notes"><span class="cui-tag cross">跨層</span> ${esc(b.crossLayerHit.label)}</div>`
            : '';
          return `<div class="cui-row" data-cui-idx="${idx}">
            <div class="cui-main">
              <div class="cui-title">${esc(formatBoardTime(b))} · ${esc(b.pillars.year)} ${esc(b.pillars.month)} ${esc(b.pillars.day)} ${esc(b.pillars.hour)}</div>
              <div class="cui-sub notes">日干${esc(b.dayStem)}（${esc(b.dayWx)}）落${esc(b.dayStemPalaceName)}${b.dayStemPalace ?? ''} · ${esc(b.result.dun)}${esc(b.result.juLabel)}</div>
              ${bgLine}
              ${crossLine}
              <div class="cui-sub notes"><span class="cui-tag sheng">生我</span> ${esc(sw)}</div>
              <div class="cui-sub notes"><span class="cui-tag ke">我剋</span> ${esc(wk)}</div>
            </div>
            <div class="cui-btns">
              <button type="button" data-act="cui-load" data-cui-idx="${idx}">載入盤</button>
            </div>
          </div>`;
        })
        .join('');
      listHtml = `<div class="cui-list">${rows}</div>`;
    }
  } else {
    listHtml = `<p class="notes">設定日時區間後按「搜尋」。硬性排除：八門／天干伏吟或反吟；日干本宮有空／刑／墓／迫；生我宮有空／刑／墓／迫；生我宮或日干本宮有庚＋丙／丙＋庚（天盤干＋地盤干或寄顯）。日干落宮生我／我剋／本宮依舊。跨層生剋＝選符號 A＋生/剋＋符號 B，比對兩符號<strong>所在宮洛書五行</strong>（非乙木／芮土等固有五行；不含中宮）。</p>`;
  }

  const layerParts: string[] = [];
  for (const l of ['gan', 'men', 'xing', 'shen'] as CuiyunLayer[]) {
    if (!cuiLayers.has(l)) continue;
    const sel = [...cuiLayerItems[l]];
    if (!sel.length) layerParts.push(`${LAYER_LABEL[l]}（不限）`);
    else layerParts.push(`${LAYER_LABEL[l]}：${sel.map((id) => layerItemLabel(l, id)).join('')}`);
  }
  const layerHint = layerParts.join(' · ') || '（未選層）';
  const relHint =
    cuiRelation === '本宮'
      ? '僅顯示日干本宮命中細項者（本宮＋生門＝落宮為生門）'
      : cuiRelation
        ? `僅顯示有「${cuiRelation}」命中者`
        : '未限生剋／本宮（顯示全部可催盤）';
  const crossHint = crossLayerHint();

  const bodyHtml = cuiyunSectionExpanded
    ? `
      <div class="cuiyun-body">
      <div class="cui-range controls">
        <span class="cui-range-label">起</span>
        ${numInput('cui-from-year', '年', cuiFrom.year, 1900, 2100)}
        ${numInput('cui-from-month', '月', cuiFrom.month, 1, 12)}
        ${numInput('cui-from-day', '日', cuiFrom.day, 1, 31)}
        ${numInput('cui-from-hour', '時', cuiFrom.hour, 0, 23)}
        ${numInput('cui-from-minute', '分', cuiFrom.minute, 0, 59)}
        <span class="cui-range-label">迄</span>
        ${numInput('cui-to-year', '年', cuiTo.year, 1900, 2100)}
        ${numInput('cui-to-month', '月', cuiTo.month, 1, 12)}
        ${numInput('cui-to-day', '日', cuiTo.day, 1, 31)}
        ${numInput('cui-to-hour', '時', cuiTo.hour, 0, 23)}
        ${numInput('cui-to-minute', '分', cuiTo.minute, 0, 59)}
        <button type="button" data-act="cui-search" class="btn-primary" ${cuiBusy ? 'disabled' : ''}>${cuiBusy ? '搜尋中…' : '搜尋'}</button>
      </div>
      <div class="cui-filter-toggle-row">
        <button type="button" data-act="cui-toggle-filters" class="cui-filter-toggle" aria-expanded="${cuiFiltersExpanded ? 'true' : 'false'}">${cuiFiltersExpanded ? '收起篩選' : '展開篩選'}</button>
        ${cuiFiltersExpanded ? '' : `<span class="notes cui-filter-summary">目前：${esc(layerHint)} · ${esc(relHint)} · ${esc(crossHint)}</span>`}
      </div>
      ${
        cuiFiltersExpanded
          ? `<div class="cui-filters-block">
      <div class="btn-row cui-filters">
        <span class="notes">層</span>
        ${layerBtn('gan', '天干')}
        ${layerBtn('men', '門')}
        ${layerBtn('xing', '星')}
        ${layerBtn('shen', '神')}
        <span class="notes" style="margin-left:0.5rem">日干落宮</span>
        ${relBtn('生我', '生我')}
        ${relBtn('我剋', '我剋')}
        ${relBtn(null, '不限')}
        ${relBtn('本宮', '本宮')}
      </div>
      <div class="cui-item-pickers">
        ${itemPickerRow('gan')}
        ${itemPickerRow('men')}
        ${itemPickerRow('xing')}
        ${itemPickerRow('shen')}
      </div>
      ${renderCrossLayerFilter()}
      <p class="notes cui-hint">目前：${esc(layerHint)} · ${esc(relHint)} · ${esc(crossHint)}。日干落宮生我／我剋／本宮＋層細項依舊。跨層生剋比對符號<strong>所在宮洛書五行</strong>（例：乙宮洛書剋芮宮洛書；合宮洛書生乙宮洛書），非固有五行。須同宮預設關。硬性排除不變。</p>
      </div>`
          : ''
      }
      ${listHtml}
      </div>`
    : '';

  return `
    <div class="cuiyun-panel no-print${cuiyunSectionExpanded ? ' is-open' : ' is-collapsed'}">
      <div class="cuiyun-header">
        <div class="cuiyun-title">可催運盤</div>
        <button type="button" data-act="cui-toggle-section" class="cui-section-toggle" aria-expanded="${cuiyunSectionExpanded ? 'true' : 'false'}">${cuiyunSectionExpanded ? '收起可催運盤' : '展開可催運盤'}</button>
      </div>
      ${bodyHtml}
    </div>`;
}

function renderOverlayControls(): string {
  const palaceOpts = [1, 2, 3, 4, 5, 6, 7, 8, 9]
    .map((n) => `<option value="${n}" ${n === overlayPalace ? 'selected' : ''}>${n}</option>`)
    .join('');
  const kindOpts = OVERLAY_KINDS.map(
    (k) => `<option value="${k}" ${k === overlayKind ? 'selected' : ''}>${k}</option>`,
  ).join('');
  const count = (overlays[overlayPalace] ?? []).length;
  return `
    <div class="overlay-bar no-print">
      <label>標註宮位
        <select id="ov-palace">${palaceOpts}</select>
      </label>
      <label>標註種類
        <select id="ov-kind">${kindOpts}</select>
      </label>
      <button type="button" data-act="ov-add">加入標註</button>
      <button type="button" data-act="ov-clear">清除該宮</button>
      <span class="notes">宮${overlayPalace}已標 ${count}/6（日干時干年干月干／日根時根年根月根）</span>
    </div>`;
}

function renderCustomerRow(): string {
  const genderOpts = [
    ['', '—'],
    ['男', '男'],
    ['女', '女'],
    ['其他', '其他'],
  ]
    .map(
      ([v, lab]) =>
        `<option value="${esc(v)}" ${customer.gender === v ? 'selected' : ''}>${esc(lab)}</option>`,
    )
    .join('');
  return `
    <div class="customer-bar">
      <div class="customer-title">顧客資料</div>
      <div class="customer-fields">
        ${textInput('cust-name', '姓名', customer.name, { placeholder: '顧客姓名' })}
        ${textInput('cust-phone', '電話', customer.phone, { placeholder: '聯絡電話' })}
        <label class="field-text">性別
          <select id="cust-gender">${genderOpts}</select>
        </label>
        ${textInput('cust-purpose', '事由', customer.purpose, { placeholder: '問事／事由' })}
        ${textInput('cust-note', '備註', customer.note, { placeholder: '其他備註', wide: true })}
      </div>
    </div>`;
}

function renderRecordActions(): string {
  const editHint = editingId
    ? `<span class="notes edit-hint">正在編輯已存記錄（儲存將覆寫）</span>`
    : '';
  return `
    <div class="record-actions no-print">
      <button type="button" data-act="save-record" class="btn-primary">儲存盤＋顧客</button>
      <button type="button" data-act="save-as-new">另存新檔</button>
      <button type="button" data-act="clear-customer">清空顧客</button>
      <button type="button" data-act="export-pdf" ${exportBusy ? 'disabled' : ''}>${
        exportBusy ? '匯出中…' : '匯出 PDF'
      }</button>
      ${editHint}
      ${statusMsg ? `<span class="status-msg">${esc(statusMsg)}</span>` : ''}
    </div>`;
}

function renderRecordsList(): string {
  const list = loadRecords();
  if (!list.length) {
    return `<div class="records-panel no-print">
      <strong>已存記錄</strong>
      <p class="notes">尚無記錄。填寫顧客資料後按「儲存盤＋顧客」，會保存在本瀏覽器（localStorage）。</p>
    </div>`;
  }
  const rows = list
    .map((r) => {
      const title = r.customer.name || '（未填姓名）';
      const phone = r.customer.phone ? ` · ${esc(r.customer.phone)}` : '';
      const purpose = r.customer.purpose ? ` · ${esc(r.customer.purpose)}` : '';
      const active = editingId === r.id ? ' active' : '';
      return `<div class="record-row${active}" data-id="${esc(r.id)}">
        <div class="record-main">
          <div class="record-title">${esc(title)}${phone}${purpose}</div>
          <div class="record-sub notes">${esc(formatSavedAt(r.savedAt))} · ${esc(r.summary.pillars)} · ${esc(r.summary.dun)}${esc(r.summary.juLabel)} · ${esc(r.summary.jieqi)}</div>
        </div>
        <div class="record-btns">
          <button type="button" data-act="load-record" data-id="${esc(r.id)}">載入</button>
          <button type="button" data-act="export-record-pdf" data-id="${esc(r.id)}">PDF</button>
          <button type="button" data-act="delete-record" data-id="${esc(r.id)}" class="btn-danger">刪除</button>
        </div>
      </div>`;
    })
    .join('');
  return `<div class="records-panel no-print">
    <strong>已存記錄</strong>（${list.length}）<span class="notes"> — 僅存於此瀏覽器，清除網站資料會消失</span>
    <div class="records-list">${rows}</div>
  </div>`;
}

function renderComboPanel(result: QimenResult): string {
  const rows = plateCombos(result);
  if (!rows.length) {
    return `<div class="combo-panel"><strong>天干組合</strong><p class="notes">盤面無對應古文組合。</p></div>`;
  }
  const list = rows
    .map(
      (r) =>
        `<div class="combo-row"><span class="combo-key">${r.palace}${esc(r.name)} ${esc(r.pair)}</span><span class="combo-text">${esc(r.text)}</span></div>`,
    )
    .join('');
  return `<div class="combo-panel"><strong>天干組合</strong>（盤面天盤干×地盤干，古文）<div class="combo-list">${list}</div></div>`;
}

function buildSummary(result: QimenResult) {
  return {
    pillars: `${result.pillars.year} ${result.pillars.month} ${result.pillars.day} ${result.pillars.hour}`,
    dun: result.dun,
    juLabel: result.juLabel,
    jieqi: result.jieqi.name,
  };
}

function doSave(asNew: boolean): void {
  readCustomerFromDom();
  const result = computeQimen(state);
  const rec = saveRecord({
    id: asNew ? undefined : editingId ?? undefined,
    customer: { ...customer },
    chart: { ...state },
    overlays: cloneOverlays(),
    summary: buildSummary(result),
  });
  editingId = rec.id;
  statusMsg = asNew ? `已另存新檔：${formatSavedAt(rec.savedAt)}` : `已儲存：${formatSavedAt(rec.savedAt)}`;
  render();
}

function doLoad(id: string): void {
  const rec = loadRecords().find((r) => r.id === id);
  if (!rec) {
    statusMsg = '找不到該記錄';
    render();
    return;
  }
  state = { ...rec.chart };
  customer = { ...emptyCustomer(), ...rec.customer };
  applyOverlays(rec.overlays);
  editingId = rec.id;
  statusMsg = `已載入：${rec.customer.name || '（未填姓名）'}（${formatSavedAt(rec.savedAt)}）`;
  render();
}

function doDelete(id: string): void {
  const rec = loadRecords().find((r) => r.id === id);
  const label = rec?.customer.name || id;
  if (!confirm(`確定刪除「${label}」？此操作無法復原。`)) return;
  deleteRecord(id);
  if (editingId === id) editingId = null;
  statusMsg = `已刪除：${label}`;
  render();
}

async function exportCurrentToPdf(): Promise<void> {
  readCustomerFromDom();
  exportBusy = true;
  statusMsg = '正在產生 PDF…';
  render();
  const area = document.getElementById('export-area');
  if (!area) {
    exportBusy = false;
    statusMsg = '找不到匯出區域';
    render();
    return;
  }
  try {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ]);
    // Re-query in case another render occurred while modules loaded
    const target = document.getElementById('export-area') ?? area;
    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#fffdf8',
      logging: false,
    });
    const img = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width >= canvas.height ? 'landscape' : 'portrait',
      unit: 'pt',
      format: 'a4',
    });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 24;
    const maxW = pageW - margin * 2;
    const maxH = pageH - margin * 2;
    const ratio = Math.min(maxW / canvas.width, maxH / canvas.height);
    const w = canvas.width * ratio;
    const h = canvas.height * ratio;
    const x = (pageW - w) / 2;
    const y = margin;
    pdf.addImage(img, 'PNG', x, y, w, h);

    const namePart = customer.name ? `_${customer.name}` : '';
    const ts = `${state.year}${String(state.month).padStart(2, '0')}${String(state.day).padStart(2, '0')}_${String(state.hour).padStart(2, '0')}${String(state.minute).padStart(2, '0')}`;
    pdf.save(`奇門排盤${namePart}_${ts}.pdf`);
    statusMsg = 'PDF 已下載';
  } catch (err) {
    console.error(err);
    statusMsg = `PDF 匯出失敗：${err instanceof Error ? err.message : String(err)}`;
  } finally {
    exportBusy = false;
    render();
  }
}

async function exportRecordToPdf(id: string): Promise<void> {
  const prev = {
    state: { ...state },
    customer: { ...customer },
    overlays: cloneOverlays(),
    editingId,
  };
  doLoad(id);
  // wait for DOM paint
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  await exportCurrentToPdf();
  // restore previous view if user was looking at something else
  // (exportCurrentToPdf already re-rendered; keep loaded record as current — more useful)
  void prev;
}

function bind(): void {
  for (const id of ['year', 'month', 'day', 'hour', 'minute'] as const) {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el) continue;
    el.addEventListener('change', () => {
      state[id] = Number(el.value);
      render();
    });
  }

  // Customer fields: update state without full re-render (keeps focus)
  for (const id of ['cust-name', 'cust-phone', 'cust-purpose', 'cust-note'] as const) {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el) continue;
    el.addEventListener('input', () => {
      const key =
        id === 'cust-name'
          ? 'name'
          : id === 'cust-phone'
            ? 'phone'
            : id === 'cust-purpose'
              ? 'purpose'
              : 'note';
      customer[key] = el.value;
    });
  }
  const genderEl = document.getElementById('cust-gender') as HTMLSelectElement | null;
  if (genderEl) {
    genderEl.addEventListener('change', () => {
      customer.gender = genderEl.value;
    });
  }

  const ovPal = document.getElementById('ov-palace') as HTMLSelectElement | null;
  if (ovPal) {
    ovPal.addEventListener('change', () => {
      overlayPalace = Number(ovPal.value);
      render();
    });
  }
  const ovKind = document.getElementById('ov-kind') as HTMLSelectElement | null;
  if (ovKind) {
    ovKind.addEventListener('change', () => {
      overlayKind = ovKind.value as OverlayKind;
    });
  }

  for (const id of [
    'cui-from-year', 'cui-from-month', 'cui-from-day', 'cui-from-hour', 'cui-from-minute',
    'cui-to-year', 'cui-to-month', 'cui-to-day', 'cui-to-hour', 'cui-to-minute',
  ] as const) {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el) continue;
    el.addEventListener('change', () => {
      readCuiFromDom();
    });
  }

  app.querySelectorAll('[data-act]').forEach((btn) => {
    // checkbox 用 change；避免 click→render 搶在 change 前重置
    if ((btn as HTMLElement).tagName === 'INPUT') return;
    btn.addEventListener('click', () => {
      const act = (btn as HTMLElement).dataset.act!;
      const rid = (btn as HTMLElement).dataset.id;
      if (act === 'now') {
        const n = new Date();
        state = {
          ...state,
          year: n.getFullYear(),
          month: n.getMonth() + 1,
          day: n.getDate(),
          hour: n.getHours(),
          minute: n.getMinutes(),
        };
      } else if (act === 'day-1' || act === 'day+1') {
        const d = new Date(state.year, state.month - 1, state.day + (act === 'day+1' ? 1 : -1));
        state.year = d.getFullYear();
        state.month = d.getMonth() + 1;
        state.day = d.getDate();
      } else if (act === 'hour-2' || act === 'hour+2') {
        const d = new Date(state.year, state.month - 1, state.day, state.hour, state.minute);
        d.setHours(d.getHours() + (act === 'hour+2' ? 2 : -2));
        state.year = d.getFullYear();
        state.month = d.getMonth() + 1;
        state.day = d.getDate();
        state.hour = d.getHours();
        state.minute = d.getMinutes();
      } else if (act === 'no-turn') {
        state.clockwise = 0;
        state.antiClockwise = 0;
      } else if (act === 'cw') {
        state.clockwise = (state.clockwise + 1) % 8;
      } else if (act === 'acw') {
        state.antiClockwise = (state.antiClockwise + 1) % 8;
      } else if (act === 'ov-add') {
        const list = overlays[overlayPalace] ?? (overlays[overlayPalace] = []);
        if (list.length >= 6) return;
        if (!list.includes(overlayKind)) list.push(overlayKind);
      } else if (act === 'ov-clear') {
        overlays[overlayPalace] = [];
      } else if (act === 'save-record') {
        doSave(false);
        return;
      } else if (act === 'save-as-new') {
        doSave(true);
        return;
      } else if (act === 'clear-customer') {
        customer = emptyCustomer();
        editingId = null;
        statusMsg = '已清空顧客資料';
      } else if (act === 'export-pdf') {
        void exportCurrentToPdf();
        return;
      } else if (act === 'load-record' && rid) {
        doLoad(rid);
        return;
      } else if (act === 'delete-record' && rid) {
        doDelete(rid);
        return;
      } else if (act === 'export-record-pdf' && rid) {
        void exportRecordToPdf(rid);
        return;
      } else if (act === 'cui-toggle-section') {
        cuiyunSectionExpanded = !cuiyunSectionExpanded;
      } else if (act === 'cui-toggle-filters') {
        cuiFiltersExpanded = !cuiFiltersExpanded;
      } else if (act === 'cui-search') {
        runCuiyunSearch();
        return;
      } else if (act === 'cui-layer') {
        const layer = (btn as HTMLElement).dataset.layer as CuiyunLayer | undefined;
        if (layer) {
          if (cuiLayers.has(layer)) {
            if (cuiLayers.size > 1) {
              cuiLayers.delete(layer);
              cuiLayerItems[layer].clear();
            }
          } else {
            cuiLayers.add(layer);
          }
          if (cuiResult) {
            runCuiyunSearch();
            return;
          }
        }
      } else if (act === 'cui-item') {
        const layer = (btn as HTMLElement).dataset.layer as CuiyunLayer | undefined;
        const item = normalizeLayerItem((btn as HTMLElement).dataset.item ?? '');
        if (layer && item && cuiLayers.has(layer)) {
          const set = cuiLayerItems[layer];
          if (set.has(item)) set.delete(item);
          else set.add(item);
          if (cuiResult) {
            runCuiyunSearch();
            return;
          }
        }
      } else if (act === 'cui-item-any') {
        const layer = (btn as HTMLElement).dataset.layer as CuiyunLayer | undefined;
        if (layer && cuiLayers.has(layer)) {
          cuiLayerItems[layer].clear();
          if (cuiResult) {
            runCuiyunSearch();
            return;
          }
        }
      } else if (act === 'cui-rel') {
        const raw = (btn as HTMLElement).dataset.rel ?? '';
        cuiRelation =
          raw === '生我' || raw === '我剋' || raw === '本宮' ? (raw as RelationFilter) : null;
        if (cuiResult) {
          runCuiyunSearch();
          return;
        }
      } else if (act === 'cui-cross-layer') {
        const side = (btn as HTMLElement).dataset.side;
        const layer = (btn as HTMLElement).dataset.layer as CuiyunLayer | undefined;
        if ((side === 'a' || side === 'b') && layer) {
          if (side === 'a') cuiCrossALayer = layer;
          else cuiCrossBLayer = layer;
        }
      } else if (act === 'cui-cross-item') {
        const side = (btn as HTMLElement).dataset.side;
        const layer = (btn as HTMLElement).dataset.layer as CuiyunLayer | undefined;
        const item = normalizeLayerItem((btn as HTMLElement).dataset.item ?? '');
        if ((side === 'a' || side === 'b') && layer && item) {
          const cur = side === 'a' ? cuiCrossA : cuiCrossB;
          const next: CrossLayerSide | null =
            cur && cur.layer === layer && cur.id === item ? null : { layer, id: item };
          if (side === 'a') {
            cuiCrossA = next;
            cuiCrossALayer = layer;
          } else {
            cuiCrossB = next;
            cuiCrossBLayer = layer;
          }
          if (cuiResult) {
            runCuiyunSearch();
            return;
          }
        }
      } else if (act === 'cui-cross-clear') {
        const side = (btn as HTMLElement).dataset.side;
        if (side === 'a') cuiCrossA = null;
        else if (side === 'b') cuiCrossB = null;
        if (cuiResult) {
          runCuiyunSearch();
          return;
        }
      } else if (act === 'cui-cross-rel') {
        const raw = (btn as HTMLElement).dataset.rel ?? '';
        cuiCrossRel = raw === '生' || raw === '剋' ? raw : null;
        if (cuiResult) {
          runCuiyunSearch();
          return;
        }
      } else if (act === 'cui-load') {
        const idx = Number((btn as HTMLElement).dataset.cuiIdx);
        const board = cuiResult?.boards[idx];
        if (board) {
          loadCuiyunBoard(board);
          return;
        }
      }
      render();
    });
  });

  // 須同宮 checkbox（非 button click）
  app.querySelectorAll<HTMLInputElement>('input[data-act="cui-cross-same"]').forEach((el) => {
    el.addEventListener('change', () => {
      cuiCrossSamePalace = el.checked;
      if (cuiResult) runCuiyunSearch();
      else render();
    });
  });
}

function render(): void {
  const result = computeQimen(state);
  const order = result.displayOrder;
  const kongNames = result.kongWangPalaces
    .map((n) => result.palaces.find((p) => p.luoshu === n)?.name ?? String(n))
    .join('');
  const maName =
    result.maXingPalace != null
      ? (result.palaces.find((p) => p.luoshu === result.maXingPalace)?.name ?? '')
      : '—';
  const fuyinBits = [result.menFuyin, result.ganFuyin].filter(Boolean).join(' · ') || '無';

  const custLine = [
    customer.name && `姓名：${customer.name}`,
    customer.phone && `電話：${customer.phone}`,
    customer.gender && `性別：${customer.gender}`,
    customer.purpose && `事由：${customer.purpose}`,
    customer.note && `備註：${customer.note}`,
  ]
    .filter(Boolean)
    .join('　');

  app.innerHTML = `
    <h1>Topwin 奇門遁甲排盤系統</h1>
    <div class="panel no-print">
      <div class="controls">
        ${numInput('year', '年', state.year, 1900, 2100)}
        ${numInput('month', '月', state.month, 1, 12)}
        ${numInput('day', '日', state.day, 1, 31)}
        ${numInput('hour', '時', state.hour, 0, 23)}
        ${numInput('minute', '分', state.minute, 0, 59)}
        <div class="btn-row">
          <button type="button" data-act="now">現在</button>
          <button type="button" data-act="day-1">前日</button>
          <button type="button" data-act="day+1">次日</button>
          <button type="button" data-act="hour-2">上一個時辰</button>
          <button type="button" data-act="hour+2">下一個時辰</button>
        </div>
      </div>
      <div class="btn-row" style="margin-top:0.75rem">
        <button type="button" data-act="no-turn" class="${
          state.clockwise === 0 && state.antiClockwise === 0 ? 'active' : ''
        }">不轉</button>
        <button type="button" data-act="cw">順轉 (${state.clockwise})</button>
        <button type="button" data-act="acw">逆轉 (${state.antiClockwise})</button>
        <span class="notes">MOD(順-逆,8) = ${result.rotation}</span>
      </div>
      ${renderCustomerRow()}
      ${renderRecordActions()}
      ${renderOverlayControls()}
    </div>

    ${renderCuiyunPanel()}

    ${renderRecordsList()}

    <div id="export-area" class="export-area">
      ${
        custLine
          ? `<div class="panel customer-print"><strong>顧客</strong> ${esc(custLine)}</div>`
          : ''
      }
      <div class="panel meta">
        <div class="meta-pillars">四柱
          <span><strong>${result.pillars.year}</strong>年</span>
          <span><strong>${result.pillars.month}</strong>月</span>
          <span><strong>${result.pillars.day}</strong>日</span>
          <span><strong>${result.pillars.hour}</strong>時</span>
        </div>
        <div>節氣 <strong>${result.jieqi.name}</strong></div>
        <div>遁 <strong>${result.dun}</strong> · <strong>${result.juLabel}</strong></div>
        <div>旬首 <strong>${result.xunshou}</strong>（${result.xunshouStem}）</div>
        <div>值符 <strong>${starLabel(result.zhifu.star)}</strong> @ ${result.zhifu.palaceName}</div>
        <div>值使 <strong>${result.zhishi.door}</strong> @ ${result.zhishi.palaceName}</div>
        <div>空亡 <strong>${kongNames || '—'}</strong></div>
        <div>馬星 <strong>${maName}</strong></div>
        <div>伏吟／反吟 <strong>${fuyinBits}</strong></div>
        <div>引干模式 <strong>${result.xunshouInZhong ? '旬首入中宮' : '常態AQ（值使→时干）'}</strong></div>
      </div>

      <div class="panel grid-wrap">
        <div>
          <div class="palace-grid">
            ${order.map((n, i) => renderPalaceCell(result, n, i)).join('')}
          </div>
          <p class="legend notes">宮格：頂中＝神（天／地八神）；右上＝地盤星＋洛書號；正中＝天盤星；右＝天盤天干（兩干橫排）／下為地盤天干；中下＝門；左中＝引干（<span style="color:#1b7a2a;font-weight:700">綠</span>，兩干直排）；底＝
            <span class="mk ma">馬</span><span class="mk po">迫</span><span class="mk mu">墓</span><span class="mk xing">刑</span><span class="mk kong">空</span>（紅）。
            標註八類：日干／時干／年干／月干／日根／時根／年根／月根（每宮最多6）。中宮＝局／旬／時＋值符值使。</p>
          ${renderComboPanel(result)}
        </div>
        <div class="side no-print">
          <div><strong>保真度</strong></div>
          <div class="fidelity">
            四柱: ${result.fidelity.pillars}<br/>
            節氣: ${result.fidelity.jieqi}<br/>
            局數: ${result.fidelity.ju}<br/>
            盤面: ${result.fidelity.plates}<br/>
            標籤: ${result.fidelity.markers}
          </div>
          ${result.notes.length ? `<p class="notes">${result.notes.join('<br/>')}</p>` : ''}
          <p class="notes">宮序為洛書號；順逆轉對照 Excel FX:GL。空亡／馬／迫／刑／墓／伏吟對照 Program 宮標。</p>
        </div>
      </div>
    </div>
  `;

  bind();
}

render();
